import React, { useState, useEffect, useCallback } from 'react';
import { useNavStep } from '../lib/NavHistory';
import {
  Globe, Building2, Package, FlaskConical, FileBarChart, Brain, Plus,
  ArrowLeft, Search, Eye, Trash2, RefreshCw, TrendingUp, Target, CheckCircle2,
} from 'lucide-react';
import { NEU } from '../neuStyles';

const API = window.zignagenixAPI;

// ─── KPI Card ──────────────────────────────────────────────────────────────

function KPICard({ icon: Icon, label, value, subtitle, color = '#2E8B98' }) {
  return (
    <div style={{
      background: NEU.bg, borderRadius: NEU.radius, padding: '20px 24px', border: 'none',
      boxShadow: NEU.shadow.sm,
      flex: '1 1 160px', minWidth: 140, display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: '#6C757D', fontWeight: 500 }}>{label}</span>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={16} color={color} />
        </div>
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: '#212529', lineHeight: 1 }}>{value}</div>
      {subtitle && <span style={{ fontSize: 11, color: '#ADB5BD' }}>{subtitle}</span>}
    </div>
  );
}

// ─── Tab Button ────────────────────────────────────────────────────────────

function TabButton({ icon: Icon, label, active, onClick }) {
  return (
    <button onClick={onClick} style={active ? {
      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: NEU.radiusSm,
      background: `linear-gradient(145deg, ${NEU.tealLight}, ${NEU.tealDark})`, color: '#fff', border: 'none',
      cursor: 'pointer', fontWeight: 600, fontSize: 13, transition: 'all 0.2s',
      boxShadow: NEU.shadow.xs,
    } : {
      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: NEU.radiusSm,
      background: NEU.bg, color: '#6C757D', border: 'none',
      cursor: 'pointer', fontWeight: 500, fontSize: 13, transition: 'all 0.2s',
      boxShadow: NEU.shadow.xs,
    }}>
      <Icon size={16} /> {label}
    </button>
  );
}

// ─── Loading Spinner ──────────────────────────────────────────────────────

function LoadingSpinner() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: '#6C757D' }}>
      <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite', marginRight: 12 }} />
      <span>Loading...</span>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── 1. DASHBOARD TAB ──────────────────────────────────────────────────────

function DashboardTab() {
  const [summary, setSummary] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAnalysisForm, setShowAnalysisForm] = useState(false);
  const [analysisType, setAnalysisType] = useState('market_trends');
  const [analysisTypes, setAnalysisTypes] = useState([]);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true);
      try {
        const [sum, reps, types] = await Promise.all([
          API.getCISummary(),
          API.getCIReports({ limit: 5 }),
          API.getCIAnalysisTypes(),
        ]);
        setSummary(sum);
        setReports(Array.isArray(reps) ? reps : []);
        setAnalysisTypes(Array.isArray(types) ? types : []);
      } catch (err) {
        console.error('Dashboard load error:', err);
      } finally {
        setLoading(false);
      }
    };
    loadDashboard();
  }, []);

  const handleRunAnalysis = async () => {
    setRunning(true);
    try {
      await API.runCIAnalysis({ type: analysisType });
      setShowAnalysisForm(false);
      // Reload summary
      const sum = await API.getCISummary();
      setSummary(sum);
    } catch (err) {
      console.error('Analysis error:', err);
    } finally {
      setRunning(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  const sectionStyle = {
    background: NEU.bg, borderRadius: NEU.radius, border: 'none', boxShadow: NEU.shadow.sm, padding: 20, marginBottom: 16,
  };

  return (
    <div>
      {/* KPI Cards */}
      {summary && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <KPICard icon={Building2} label="Competitors" value={summary.competitorCount || 0} color="#2E8B98" />
          <KPICard icon={Package} label="Products" value={summary.productCount || 0} color="#62BB46" />
          <KPICard icon={FlaskConical} label="Test Comparisons" value={summary.testCount || 0} color="#F26522" />
          <KPICard icon={FileBarChart} label="Reports" value={summary.reportCount || 0} color="#256F7A" />
          <KPICard icon={Brain} label="Analyses" value={summary.analysisCount || 0} color="#2E8B98" />
        </div>
      )}

      {/* Recent Reports */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: '#212529' }}>Recent Reports</h3>
        </div>
        {reports.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {reports.map((r, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '12px', borderRadius: NEU.radiusSm, background: NEU.bg, border: 'none',
                boxShadow: `inset 3px 0 0 ${NEU.teal}, ${NEU.shadow.insetSm}`,
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#212529' }}>{r.title}</div>
                  <div style={{ fontSize: 11, color: '#6C757D' }}>{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : 'N/A'}</div>
                </div>
                <Eye size={16} style={{ cursor: 'pointer', color: '#2E8B98' }} />
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: '#ADB5BD', fontSize: 13, textAlign: 'center', padding: 40 }}>No reports yet</div>
        )}
      </div>

      {/* Run Analysis */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: '#212529' }}>AI Analysis</h3>
          {!showAnalysisForm && (
            <button onClick={() => setShowAnalysisForm(true)} style={{
              background: '#2E8B98', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px',
              cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4,
            }}>
              <Brain size={14} /> Run Analysis
            </button>
          )}
        </div>

        {showAnalysisForm ? (
          <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
            <select value={analysisType} onChange={(e) => setAnalysisType(e.target.value)} style={{
              padding: '8px 12px', borderRadius: NEU.radiusSm, border: 'none', boxShadow: NEU.shadow.insetSm, fontSize: 12, background: NEU.bg,
            }}>
              {analysisTypes.map((t) => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleRunAnalysis} disabled={running} style={{
                background: '#2E8B98', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px',
                cursor: running ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 500, opacity: running ? 0.6 : 1,
              }}>
                {running ? 'Running...' : 'Execute'}
              </button>
              <button onClick={() => setShowAnalysisForm(false)} style={{
                background: NEU.bg, color: '#6C757D', border: 'none', borderRadius: NEU.radiusSm, padding: '8px 16px',
                cursor: 'pointer', fontSize: 12, boxShadow: NEU.shadow.xs,
              }}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div style={{ color: '#ADB5BD', fontSize: 13, textAlign: 'center', padding: 20 }}>No analysis running</div>
        )}
      </div>
    </div>
  );
}

// ─── 2. COMPETITORS TAB ────────────────────────────────────────────────────

function CompetitorsTab() {
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedCompetitor, setSelectedCompetitor] = useState(null);
  const [formData, setFormData] = useState({ companyName: '', headquarters: '', specialties: '' });
  const [search, setSearch] = useState('');

  // #65 Phase C2b-1: the competitor detail is this tab's master->detail sub-view.
  // Register ONE Back step so global Back returns to the competitor LIST before
  // navigating away — same handler the in-view "← Back to List" button uses.
  // MUST be unconditional and ABOVE the `if (loading)` / `if (selectedCompetitor)`
  // early returns below (rules-of-hooks). LITERAL label — never a company name/id.
  useNavStep({
    active: !!selectedCompetitor,
    label: 'Competitor',
    onBack: () => setSelectedCompetitor(null),
  });

  useEffect(() => {
    loadCompetitors();
  }, []);

  const loadCompetitors = async () => {
    setLoading(true);
    try {
      const data = await API.getCICompetitors({ limit: 100 });
      setCompetitors(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Competitors load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCompetitor = async () => {
    if (!formData.companyName) return;
    try {
      const result = await API.createCICompetitor(formData);
      if (result && result.error) {
        alert('Error: ' + (result.message || 'Unknown error'));
        return;
      }
      setFormData({ companyName: '', headquarters: '', specialties: '' });
      setShowForm(false);
      loadCompetitors();
    } catch (err) {
      console.error('Create error:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete competitor?')) return;
    try {
      await API.deactivateCICompetitor(id);
      loadCompetitors();
      setSelectedCompetitor(null);
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const filtered = competitors.filter(c =>
    (c.company_name || c.name || '').toLowerCase().includes(search.toLowerCase())
  );

  const sectionStyle = {
    background: NEU.bg, borderRadius: NEU.radius, border: 'none', boxShadow: NEU.shadow.sm, padding: 20,
  };

  if (loading) return <LoadingSpinner />;

  if (selectedCompetitor) {
    return (
      <div>
        <button onClick={() => setSelectedCompetitor(null)} style={{
          background: 'none', border: 'none', cursor: 'pointer', color: '#2E8B98', fontSize: 13,
          display: 'flex', alignItems: 'center', gap: 6, marginBottom: 20, fontWeight: 500,
        }}>
          <ArrowLeft size={16} /> Back to List
        </button>

        <div style={sectionStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: '#212529' }}>{selectedCompetitor.company_name || selectedCompetitor.name}</h2>
              <p style={{ fontSize: 12, color: '#6C757D', margin: '4px 0' }}>Headquarters: {selectedCompetitor.headquarters || 'N/A'}</p>
            </div>
            <button onClick={() => handleDelete(selectedCompetitor.id)} style={{
              background: '#fef2f2', color: '#DC3545', border: 'none', borderRadius: NEU.radiusSm,
              padding: '6px 12px', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4,
              boxShadow: NEU.shadow.xs,
            }}>
              <Trash2 size={14} /> Delete
            </button>
          </div>
          <p style={{ fontSize: 13, color: '#212529', lineHeight: 1.6 }}>{selectedCompetitor.specialties || 'No specialties listed'}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
          <Search size={16} color="#6C757D" />
          <input type="text" placeholder="Search competitors..." value={search} onChange={(e) => setSearch(e.target.value)} style={{
            flex: 1, padding: '8px 12px', borderRadius: NEU.radiusSm, border: 'none', boxShadow: NEU.shadow.insetSm, fontSize: 12, background: NEU.bg,
          }} />
        </div>
        <button onClick={() => setShowForm(!showForm)} style={{
          background: `linear-gradient(145deg, ${NEU.tealLight}, ${NEU.tealDark})`, color: '#fff', border: 'none', borderRadius: NEU.radiusSm, padding: '8px 12px',
          cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, marginLeft: 8, boxShadow: NEU.shadow.xs,
        }}>
          <Plus size={14} /> Add Competitor
        </button>
      </div>

      {showForm && (
        <div style={Object.assign({}, { marginBottom: 16 }, sectionStyle)}>
          <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 12px 0', color: '#212529' }}>New Competitor</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input type="text" placeholder="Company Name" value={formData.companyName} onChange={(e) => setFormData({ ...formData, companyName: e.target.value })} style={{
              padding: '8px 12px', borderRadius: NEU.radiusSm, border: 'none', boxShadow: NEU.shadow.insetSm, fontSize: 12, background: NEU.bg,
            }} />
            <input type="text" placeholder="Headquarters" value={formData.headquarters} onChange={(e) => setFormData({ ...formData, headquarters: e.target.value })} style={{
              padding: '8px 12px', borderRadius: NEU.radiusSm, border: 'none', boxShadow: NEU.shadow.insetSm, fontSize: 12, background: NEU.bg,
            }} />
            <textarea placeholder="Specialties" value={formData.specialties} onChange={(e) => setFormData({ ...formData, specialties: e.target.value })} style={{
              padding: '8px 12px', borderRadius: NEU.radiusSm, border: 'none', boxShadow: NEU.shadow.insetSm, fontSize: 12, minHeight: 60, fontFamily: 'inherit', background: NEU.bg,
            }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleAddCompetitor} style={{
                background: `linear-gradient(145deg, ${NEU.tealLight}, ${NEU.tealDark})`, color: '#fff', border: 'none', borderRadius: NEU.radiusSm, padding: '8px 16px',
                cursor: 'pointer', fontSize: 12, fontWeight: 500, boxShadow: NEU.shadow.xs,
              }}>
                Create
              </button>
              <button onClick={() => setShowForm(false)} style={{
                background: NEU.bg, color: '#6C757D', border: 'none', borderRadius: NEU.radiusSm, padding: '8px 16px',
                cursor: 'pointer', fontSize: 12, boxShadow: NEU.shadow.xs,
              }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
        {filtered.map((c, i) => (
          <div key={i} onClick={() => setSelectedCompetitor(c)} style={Object.assign({}, sectionStyle, {
            cursor: 'pointer', transition: 'all 0.2s', borderLeft: '3px solid #2E8B98',
          })} onMouseEnter={(e) => e.currentTarget.style.boxShadow = NEU.shadow.md} onMouseLeave={(e) => e.currentTarget.style.boxShadow = NEU.shadow.sm}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <div style={{ width: 40, height: 40, borderRadius: 8, background: '#2E8B9818', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Building2 size={20} color="#2E8B98" />
              </div>
              <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#212529' }}>{c.company_name || c.name}</h4>
            </div>
            <p style={{ fontSize: 11, color: '#6C757D', margin: 0 }}>{c.headquarters || 'No location'}</p>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div style={{ textAlign: 'center', color: '#ADB5BD', fontSize: 13, padding: 40 }}>No competitors found</div>
      )}
    </div>
  );
}

// ─── 3. PRODUCTS TAB ───────────────────────────────────────────────────────

function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({ competitorId: '', productName: '', productCategory: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [prods, comps] = await Promise.all([
        API.getCIProducts({ limit: 100 }),
        API.getCICompetitors({ limit: 100 }),
      ]);
      setProducts(Array.isArray(prods) ? prods : []);
      setCompetitors(Array.isArray(comps) ? comps : []);
    } catch (err) {
      console.error('Products load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = async () => {
    if (!formData.competitorId || !formData.productName) return;
    try {
      const result = await API.createCIProduct(formData);
      if (result && result.error) {
        alert('Error: ' + (result.message || 'Unknown error'));
        return;
      }
      setFormData({ competitorId: '', productName: '', productCategory: '' });
      setShowForm(false);
      loadData();
    } catch (err) {
      console.error('Create error:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete product?')) return;
    try {
      await API.deactivateCIProduct(id);
      loadData();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const filtered = products.filter(p =>
    (p.product_name || p.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.competitor_name || p.competitorName || '').toLowerCase().includes(search.toLowerCase())
  );

  const sectionStyle = {
    background: NEU.bg, borderRadius: NEU.radius, border: 'none', boxShadow: NEU.shadow.sm, padding: 20,
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
          <Search size={16} color="#6C757D" />
          <input type="text" placeholder="Search products..." value={search} onChange={(e) => setSearch(e.target.value)} style={{
            flex: 1, padding: '8px 12px', borderRadius: NEU.radiusSm, border: 'none', boxShadow: NEU.shadow.insetSm, fontSize: 12, background: NEU.bg,
          }} />
        </div>
        <button onClick={() => setShowForm(!showForm)} style={{
          background: `linear-gradient(145deg, ${NEU.tealLight}, ${NEU.tealDark})`, color: '#fff', border: 'none', borderRadius: NEU.radiusSm, padding: '8px 12px',
          cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, marginLeft: 8, boxShadow: NEU.shadow.xs,
        }}>
          <Plus size={14} /> Add Product
        </button>
      </div>

      {showForm && (
        <div style={Object.assign({}, { marginBottom: 16 }, sectionStyle)}>
          <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 12px 0', color: '#212529' }}>New Product</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <select value={formData.competitorId} onChange={(e) => setFormData({ ...formData, competitorId: e.target.value })} style={{
              padding: '8px 12px', borderRadius: NEU.radiusSm, border: 'none', boxShadow: NEU.shadow.insetSm, fontSize: 12, background: NEU.bg,
            }}>
              <option value="">Select Competitor</option>
              {competitors.map((c, i) => (
                <option key={i} value={c.id}>{c.company_name || c.name}</option>
              ))}
            </select>
            <input type="text" placeholder="Product Name" value={formData.productName || formData.name || ''} onChange={(e) => setFormData({ ...formData, productName: e.target.value })} style={{
              padding: '8px 12px', borderRadius: NEU.radiusSm, border: 'none', boxShadow: NEU.shadow.insetSm, fontSize: 12, background: NEU.bg,
            }} />
            <input type="text" placeholder="Category" value={formData.productCategory} onChange={(e) => setFormData({ ...formData, productCategory: e.target.value })} style={{
              padding: '8px 12px', borderRadius: NEU.radiusSm, border: 'none', boxShadow: NEU.shadow.insetSm, fontSize: 12, background: NEU.bg,
            }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleAddProduct} style={{
                background: `linear-gradient(145deg, ${NEU.tealLight}, ${NEU.tealDark})`, color: '#fff', border: 'none', borderRadius: NEU.radiusSm, padding: '8px 16px',
                cursor: 'pointer', fontSize: 12, fontWeight: 500, boxShadow: NEU.shadow.xs,
              }}>
                Create
              </button>
              <button onClick={() => setShowForm(false)} style={{
                background: NEU.bg, color: '#6C757D', border: 'none', borderRadius: NEU.radiusSm, padding: '8px 16px',
                cursor: 'pointer', fontSize: 12, boxShadow: NEU.shadow.xs,
              }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={sectionStyle}>
        {filtered.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <div style={{
              display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 0.5fr', gap: 12, padding: '8px 0',
              borderBottom: 'none', boxShadow: `inset 0 -1px 0 ${NEU.teal}`, fontWeight: 600, color: '#6C757D', fontSize: 11,
            }}>
              <span>Product</span><span>Competitor</span><span>Category</span><span></span>
            </div>
            {filtered.map((p, i) => (
              <div key={i} style={{
                display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 0.5fr', gap: 12, padding: '8px 0',
                borderBottom: 'none', boxShadow: `inset 0 -1px 0 rgba(186,199,216,0.25)`, alignItems: 'center', color: '#212529',
              }}>
                <span style={{ fontWeight: 500 }}>{p.product_name || p.name}</span>
                <span style={{ fontSize: 12, color: '#6C757D' }}>{p.competitor_name || p.competitorName || 'N/A'}</span>
                <span style={{ fontSize: 12, color: '#6C757D' }}>{p.product_category || p.category || 'N/A'}</span>
                <button onClick={() => handleDelete(p.id)} style={{
                  background: 'none', border: 'none', cursor: 'pointer', color: '#DC3545',
                  padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', color: '#ADB5BD', fontSize: 13, padding: 40 }}>No products found</div>
        )}
      </div>
    </div>
  );
}

// ─── 4. HEAD-TO-HEAD TAB ───────────────────────────────────────────────────

function HeadToHeadTab() {
  const [tests, setTests] = useState([]);
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({
    testName: '', competitorId: '', ourScore: '', competitorScore: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [ts, comps] = await Promise.all([
        API.getCITests({ limit: 100 }),
        API.getCICompetitors({ limit: 100 }),
      ]);
      setTests(Array.isArray(ts) ? ts : []);
      setCompetitors(Array.isArray(comps) ? comps : []);
    } catch (err) {
      console.error('Tests load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTest = async () => {
    if (!formData.testName || !formData.competitorId) return;
    try {
      await API.createCITest(formData);
      setFormData({ testName: '', competitorId: '', ourScore: '', competitorScore: '' });
      setShowForm(false);
      loadData();
    } catch (err) {
      console.error('Create error:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete test?')) return;
    try {
      await API.deactivateCITest(id);
      loadData();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const filtered = tests.filter(t =>
    t.testName?.toLowerCase().includes(search.toLowerCase())
  );

  const sectionStyle = {
    background: NEU.bg, borderRadius: NEU.radius, border: 'none', boxShadow: NEU.shadow.sm, padding: 20,
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
          <Search size={16} color="#6C757D" />
          <input type="text" placeholder="Search tests..." value={search} onChange={(e) => setSearch(e.target.value)} style={{
            flex: 1, padding: '8px 12px', borderRadius: NEU.radiusSm, border: 'none', boxShadow: NEU.shadow.insetSm, fontSize: 12, background: NEU.bg,
          }} />
        </div>
        <button onClick={() => setShowForm(!showForm)} style={{
          background: `linear-gradient(145deg, ${NEU.tealLight}, ${NEU.tealDark})`, color: '#fff', border: 'none', borderRadius: NEU.radiusSm, padding: '8px 12px',
          cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, marginLeft: 8, boxShadow: NEU.shadow.xs,
        }}>
          <Plus size={14} /> Create Comparison
        </button>
      </div>

      {showForm && (
        <div style={Object.assign({}, { marginBottom: 16 }, sectionStyle)}>
          <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 12px 0', color: '#212529' }}>New Comparison</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input type="text" placeholder="Test Name" value={formData.testName} onChange={(e) => setFormData({ ...formData, testName: e.target.value })} style={{
              padding: '8px 12px', borderRadius: NEU.radiusSm, border: 'none', boxShadow: NEU.shadow.insetSm, fontSize: 12, background: NEU.bg,
            }} />
            <select value={formData.competitorId} onChange={(e) => setFormData({ ...formData, competitorId: e.target.value })} style={{
              padding: '8px 12px', borderRadius: NEU.radiusSm, border: 'none', boxShadow: NEU.shadow.insetSm, fontSize: 12, background: NEU.bg,
            }}>
              <option value="">Select Competitor</option>
              {competitors.map((c, i) => (
                <option key={i} value={c.id}>{c.company_name || c.name}</option>
              ))}
            </select>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <input type="number" placeholder="ZignaGenix Score" value={formData.ourScore} onChange={(e) => setFormData({ ...formData, ourScore: e.target.value })} style={{
                padding: '8px 12px', borderRadius: NEU.radiusSm, border: 'none', boxShadow: NEU.shadow.insetSm, fontSize: 12, background: NEU.bg,
              }} />
              <input type="number" placeholder="Competitor Score" value={formData.competitorScore} onChange={(e) => setFormData({ ...formData, competitorScore: e.target.value })} style={{
                padding: '8px 12px', borderRadius: NEU.radiusSm, border: 'none', boxShadow: NEU.shadow.insetSm, fontSize: 12, background: NEU.bg,
              }} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleAddTest} style={{
                background: `linear-gradient(145deg, ${NEU.tealLight}, ${NEU.tealDark})`, color: '#fff', border: 'none', borderRadius: NEU.radiusSm, padding: '8px 16px',
                cursor: 'pointer', fontSize: 12, fontWeight: 500, boxShadow: NEU.shadow.xs,
              }}>
                Create
              </button>
              <button onClick={() => setShowForm(false)} style={{
                background: NEU.bg, color: '#6C757D', border: 'none', borderRadius: NEU.radiusSm, padding: '8px 16px',
                cursor: 'pointer', fontSize: 12, boxShadow: NEU.shadow.xs,
              }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={sectionStyle}>
        {filtered.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <div style={{
              display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 1fr 0.5fr', gap: 12, padding: '8px 0',
              borderBottom: 'none', boxShadow: `inset 0 -1px 0 ${NEU.teal}`, fontWeight: 600, color: '#6C757D', fontSize: 11,
            }}>
              <span>Test</span><span>Competitor</span><span>ZignaGenix</span><span>Competitor</span><span></span>
            </div>
            {filtered.map((t, i) => {
              const ourScore = parseFloat(t.ourScore);
              const compScore = parseFloat(t.competitorScore);
              const weWin = ourScore > compScore;
              return (
                <div key={i} style={{
                  display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 1fr 0.5fr', gap: 12, padding: '8px 0',
                  borderBottom: 'none', boxShadow: `inset 0 -1px 0 rgba(186,199,216,0.25)`, alignItems: 'center', color: '#212529',
                }}>
                  <span style={{ fontWeight: 500 }}>{t.testName}</span>
                  <span style={{ fontSize: 12, color: '#6C757D' }}>{t.competitorName || 'N/A'}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontWeight: 500 }}>{ourScore}</span>
                    {weWin && <CheckCircle2 size={14} color="#62BB46" />}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontWeight: 500 }}>{compScore}</span>
                    {!weWin && <AlertTriangleIcon size={14} color="#F26522" />}
                  </div>
                  <button onClick={() => handleDelete(t.id)} style={{
                    background: 'none', border: 'none', cursor: 'pointer', color: '#DC3545',
                    padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ textAlign: 'center', color: '#ADB5BD', fontSize: 13, padding: 40 }}>No tests found</div>
        )}
      </div>
    </div>
  );
}

function AlertTriangleIcon({ size, color }) {
  return <div style={{ width: size, height: size, color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>!</div>;
}

// ─── 5. REPORTS & AI TAB ───────────────────────────────────────────────────

function ReportsTab() {
  const [reports, setReports] = useState([]);
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReportForm, setShowReportForm] = useState(false);
  const [analysisTypes, setAnalysisTypes] = useState([]);
  const [reportFormData, setReportFormData] = useState({ title: '', scope: 'market_analysis' });
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [reps, hist, types] = await Promise.all([
        API.getCIReports({ limit: 20 }),
        API.getCIAnalysisHistory({ limit: 20 }),
        API.getCIAnalysisTypes(),
      ]);
      setReports(Array.isArray(reps) ? reps : []);
      setAnalysisHistory(Array.isArray(hist) ? hist : []);
      setAnalysisTypes(Array.isArray(types) ? types : []);
    } catch (err) {
      console.error('Reports load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!reportFormData.title) return;
    setGenerating(true);
    try {
      await API.generateCIReport(reportFormData);
      setReportFormData({ title: '', scope: 'market_analysis' });
      setShowReportForm(false);
      loadData();
    } catch (err) {
      console.error('Generate error:', err);
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteReport = async (id) => {
    if (!window.confirm('Delete report?')) return;
    try {
      await API.deactivateCIReport(id);
      loadData();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const sectionStyle = {
    background: NEU.bg, borderRadius: NEU.radius, border: 'none', boxShadow: NEU.shadow.sm, padding: 20, marginBottom: 16,
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {/* Reports Section */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: '#212529' }}>CI Reports</h3>
          <button onClick={() => setShowReportForm(!showReportForm)} style={{
            background: `linear-gradient(145deg, ${NEU.tealLight}, ${NEU.tealDark})`, color: '#fff', border: 'none', borderRadius: NEU.radiusSm, padding: '6px 12px',
            cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, boxShadow: NEU.shadow.xs,
          }}>
            <Plus size={14} /> Generate Report
          </button>
        </div>

        {showReportForm && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16, padding: 12, background: NEU.bg, borderRadius: NEU.radiusSm, boxShadow: NEU.shadow.insetSm }}>
            <input type="text" placeholder="Report Title" value={reportFormData.title} onChange={(e) => setReportFormData({ ...reportFormData, title: e.target.value })} style={{
              padding: '8px 12px', borderRadius: NEU.radiusSm, border: 'none', boxShadow: NEU.shadow.insetSm, fontSize: 12, background: NEU.bg,
            }} />
            <select value={reportFormData.scope} onChange={(e) => setReportFormData({ ...reportFormData, scope: e.target.value })} style={{
              padding: '8px 12px', borderRadius: NEU.radiusSm, border: 'none', boxShadow: NEU.shadow.insetSm, fontSize: 12, background: NEU.bg,
            }}>
              <option value="market_analysis">Market Analysis</option>
              <option value="product_comparison">Product Comparison</option>
              <option value="pricing_analysis">Pricing Analysis</option>
              <option value="executive_summary">Executive Summary</option>
            </select>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleGenerateReport} disabled={generating} style={{
                background: `linear-gradient(145deg, ${NEU.tealLight}, ${NEU.tealDark})`, color: '#fff', border: 'none', borderRadius: NEU.radiusSm, padding: '8px 16px',
                cursor: generating ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 500, opacity: generating ? 0.6 : 1, boxShadow: NEU.shadow.xs,
              }}>
                {generating ? 'Generating...' : 'Generate'}
              </button>
              <button onClick={() => setShowReportForm(false)} style={{
                background: NEU.bg, color: '#6C757D', border: 'none', borderRadius: NEU.radiusSm, padding: '8px 16px',
                cursor: 'pointer', fontSize: 12, boxShadow: NEU.shadow.xs,
              }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {reports.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {reports.map((r, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '12px', borderRadius: NEU.radiusSm, background: NEU.bg, border: 'none',
                boxShadow: `inset 3px 0 0 ${NEU.teal}, ${NEU.shadow.insetSm}`,
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#212529' }}>{r.title}</div>
                  <div style={{ fontSize: 11, color: '#6C757D' }}>{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : 'N/A'}</div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Eye size={14} style={{ cursor: 'pointer', color: '#2E8B98' }} />
                  <button onClick={() => handleDeleteReport(r.id)} style={{
                    background: 'none', border: 'none', cursor: 'pointer', color: '#DC3545', padding: 0,
                  }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: '#ADB5BD', fontSize: 13, textAlign: 'center', padding: 40 }}>No reports yet</div>
        )}
      </div>

      {/* Analysis History */}
      <div style={sectionStyle}>
        <h3 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 16px 0', color: '#212529' }}>Analysis History</h3>
        {analysisHistory.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {analysisHistory.slice(0, 10).map((a, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '12px', borderRadius: NEU.radiusSm, background: NEU.bg, boxShadow: NEU.shadow.insetSm,
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#212529' }}>{a.type || 'Analysis'}</div>
                  <div style={{ fontSize: 11, color: '#6C757D' }}>{a.createdAt ? new Date(a.createdAt).toLocaleDateString() : 'N/A'}</div>
                </div>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 4, fontSize: 11,
                  padding: '4px 8px', borderRadius: 4, background: a.status === 'completed' ? '#d1fae5' : '#fef3c7',
                  color: a.status === 'completed' ? '#065f46' : '#92400e',
                }}>
                  {a.status === 'completed' ? <CheckCircle2 size={12} /> : <RefreshCw size={12} />}
                  {a.status || 'pending'}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: '#ADB5BD', fontSize: 13, textAlign: 'center', padding: 40 }}>No analysis history</div>
        )}
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ────────────────────────────────────────────────────────

export default function CompetitiveIntelPage({ user }) {
  const [activeTab, setActiveTab] = useState('dashboard');

  const tabs = [
    { id: 'dashboard', icon: Globe, label: 'Dashboard', component: DashboardTab },
    { id: 'competitors', icon: Building2, label: 'Competitors', component: CompetitorsTab },
    { id: 'products', icon: Package, label: 'Products', component: ProductsTab },
    { id: 'headtohead', icon: FlaskConical, label: 'Head-to-Head', component: HeadToHeadTab },
    { id: 'reports', icon: FileBarChart, label: 'Reports & AI', component: ReportsTab },
  ];

  const ActiveComponent = tabs.find(t => t.id === activeTab)?.component;

  return (
    <div className="page" style={{ padding: 24, overflowY: 'auto', background: '#F8F9FA', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg, #2E8B98, #1d4ed8)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Target size={22} color="#fff" />
          </div>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: '#212529' }}>Competitive Intelligence</h1>
            <p style={{ fontSize: 12, color: '#6C757D', margin: 0 }}>Track competitors, products, and market analysis</p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ display: 'flex', gap: 8, borderBottom: 'none', boxShadow: `inset 0 -1px 0 ${NEU.teal}`, paddingBottom: 12 }}>
          {tabs.map(tab => (
            <TabButton
              key={tab.id}
              icon={tab.icon}
              label={tab.label}
              active={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            />
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {ActiveComponent && <ActiveComponent />}
    </div>
  );
}
